import { c as _c } from "react/compiler-runtime";
// `<HreflangAlternates>` — server-rendered `<link rel="alternate" hreflang>`
// tags for every supported locale plus `x-default`.
//
// Why this exists: Next.js's `Metadata.alternates.languages` field has a
// known dedup behaviour (combination of metadata-resolver + React 19
// `<link>` hoisting) that collapses every entry into the single
// `x-default` tag whenever all locales resolve to the same canonical
// URL. With `localePrefix: 'never'` every Morfi locale serves the same
// URL, so the metadata API silently emits only one tag — invisible to
// crawlers.
//
// Direct JSX `<link>` rendering inside a Server Component bypasses both
// the metadata resolver AND React 19's hoisting dedup (the elements
// stream as part of the page tree, not React's metadata layer), so all
// 22 tags survive into the rendered HTML.
//
// Detail pages (markets/launchpad/portfolio/thesis/trade/etc.) call this
// via `<PageSeo>`. Listing pages (homepage, /markets, /social) and
// content pages (/about, /learn/*, /compare/*, /privacy) render it
// directly with their own canonical path. Any new SEO-bearing page MUST
// emit hreflang — see `lib/seo/_hreflang.md` (forthcoming) or audit
// rule `HREFLANG_MISSING`.

import { locales } from '@/i18n/config';
interface HreflangAlternatesProps {
  /** Absolute canonical URL for this page. Same value across every
   *  locale variant — `localePrefix: 'never'` is the architectural
   *  reason all 22 emitted tags share an href. The hreflang attribute
   *  is what crawlers parse; the href just confirms the URL. */
  canonicalUrl: string;
}
export function HreflangAlternates(t0) {
  const $ = _c(6);
  const {
    canonicalUrl
  } = t0;
  let t1;
  let t2;
  if ($[0] !== canonicalUrl) {
    t1 = <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />;
    t2 = locales.map(locale => <link key={locale} rel="alternate" hrefLang={locale} href={canonicalUrl} />);
    $[0] = canonicalUrl;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = <>{t1}{t2}</>;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}